import React, { useState } from 'react';
import {
    GestionDirigeantList,
    GestionDirigeant,
    GestionDirigeantWidgetCallbackArgs,
    useModal,
} from '@europrocurement/flexy-components';
import { FaOptionIcon, informationIcon } from '@europrocurement/l2d-icons';

import { Box } from '@mui/system';
import {
    Button,
    ClickAwayListener,
    Grid,
    IconButton,
    styled,
    Tooltip,
    tooltipClasses,
    TooltipProps,
    Typography,
    useTheme,
} from '@mui/material';
import { CustomOptionResolver, CustomOptionResolvers } from '@europrocurement/flexy-form';
import {
    FormulaireFormaliteApiObject,
    useFormaliteService,
    useFormulaireService,
} from '@europrocurement/l2d-domain';
import { useParcoursFormalite } from '@europrocurement/l2d-modules/modules/ParcoursFormalite/provider';
import { FormulaireFormaliteJsonld } from '@europrocurement/l2d-domain/openApi/ApiFormulaire';
import { castIriToId } from '@europrocurement/l2d-utils';
import { useSnackbar } from 'notistack';
import { jsonForm } from '@europrocurement/flexy-form/utils/types/formTypes';
import { faUserPlus } from '@fortawesome/sharp-regular-svg-icons';

// eslint-disable-next-line import/no-cycle
import { customItemJsonProps } from '../customItemRenderer';
import { useListeSelectionDataResolver } from '../../../hooks/useListeSelectionDataResolver';
import GestionDirigeantJson from './GestionDirigeant.json';
import GestionDirigeantAjoutJson from './GestionDirigeantAjout.json';
import GestionDirigeantAdresseJson from './GestionDirigeantAdresse.json';

import GestionDirigeantModal from './GestionDirigeantModal';

export const GestionDirigeantView: React.FunctionComponent<customItemJsonProps> = function ({
    formContext,
    props,
    visibility,
    mode,
}) {
    formContext.watch(props.name);

    const { enqueueSnackbar } = useSnackbar();
    const { getCurrentPresta } = useParcoursFormalite();
    const presta = getCurrentPresta();
    const { formulairesApi } = useFormulaireService();
    const { formaliteApi } = useFormaliteService();
    const createListeSelectionDataResolver = useListeSelectionDataResolver();
    const [form, setForm] = useState<FormulaireFormaliteApiObject | null>(null);
    const { modalActions } = useModal();
    const theme = useTheme();

    React.useEffect(() => {
        if (!form && presta) {
            const fetchForm = async () => {
                const resFormality = await formaliteApi.apiFormalitesIdGet({
                    id: `${castIriToId(presta.formalite as unknown as string)}`,
                });
                if (resFormality.data.formulaire) {
                    const resFormulaire = await formulairesApi.apiFormulaireFormalitesIdGet({
                        id: `${castIriToId(resFormality.data.formulaire)}`,
                    });
                    if (resFormulaire.data) {
                        setForm(resFormulaire.data as FormulaireFormaliteJsonld);
                    }
                } else {
                    enqueueSnackbar("La formalité n'a pas de formulaire", { variant: 'error' });
                    throw new Error('no formulaire on formality');
                }
            };
            fetchForm();
        }
    }, [presta, enqueueSnackbar, form, formaliteApi, formulairesApi]);

    const formjson = GestionDirigeantJson;
    const formjsonAjout = GestionDirigeantAjoutJson;
    const formjsonAdresse = GestionDirigeantAdresseJson;

    const createFakeListResolvers = () => {
        const listeSelectionDataResolver: CustomOptionResolver = () => [
            { label: 'Option 1', value: 1 },
            { label: 'Option 2', value: 2 },
            { label: 'Option 3', value: 3 },
            { label: 'Option 4', value: 4 },
            { label: 'Option 5', value: 5 },
            { label: 'Option 6', value: 6 },
            { label: 'Option 7', value: 7 },
            { label: 'Option 8', value: 8 },
            { label: 'Option 9', value: 9 },
            { label: 'Option 10', value: 10 },
        ];
        return listeSelectionDataResolver;
    };

    const customOptionResolvers: CustomOptionResolvers = {
        listeSelectionDataResolver: form
            ? createListeSelectionDataResolver(form)
            : createFakeListResolvers(),
    };

    const onAdd = () => {
        modalActions.call(
            <GestionDirigeantModal
                onChange={async (curr, pagelenth, data) => {
                    formContext.setValue(props.name, [
                        ...(formContext.getValues(props.name) || []),
                        {
                            gestionDirigeant: {
                                ...data,
                            },
                        },
                    ]);
                    modalActions.close();
                }}
                json={formjsonAjout as jsonForm}
                defaultValues={{}}
                customOptionResolvers={customOptionResolvers}
                visibility={visibility}
            />,
            false,
            true,
        );
    };

    const onPreview: GestionDirigeantWidgetCallbackArgs = (gestionDirigeant, index) => {
        modalActions.call(
            <GestionDirigeantModal
                onChange={async (curr, pagelenth, data) => {
                    const newValues = [...formContext.getValues(props.name)];
                    newValues.splice(index, 1, {
                        gestionDirigeant: {
                            ...data,
                        },
                    });
                    formContext.setValue(props.name, newValues);
                    modalActions.close();
                }}
                json={formjson as jsonForm}
                defaultValues={{
                    ...gestionDirigeant,
                }}
                customOptionResolvers={customOptionResolvers}
                visibility={visibility}
            />,
            false,
            true,
        );
    };

    const onPreviewAdresse: GestionDirigeantWidgetCallbackArgs = (gestionDirigeant, index) => {
        modalActions.call(
            <GestionDirigeantModal
                onChange={async (curr, pagelenth, data) => {
                    const newValues = [...formContext.getValues(props.name)];
                    newValues.splice(index, 1, {
                        gestionDirigeant: {
                            ...data,
                        },
                    });
                    formContext.setValue(props.name, newValues);
                    modalActions.close();
                }}
                json={formjsonAdresse as jsonForm}
                defaultValues={{
                    ...gestionDirigeant,
                }}
                customOptionResolvers={customOptionResolvers}
                visibility={visibility}
            />,
            false,
            true,
        );
    };

    const LightTooltip = styled(({ className, ...propsTooltip }: TooltipProps) =>
        propsTooltip.onClose ? (
            <ClickAwayListener onClickAway={propsTooltip.onClose}>
                <Tooltip
                    {...propsTooltip}
                    classes={{ popper: className }}
                    PopperProps={{ sx: { '.MuiTooltip-tooltip': { bgcolor: 'green' } } }}
                />
            </ClickAwayListener>
        ) : (
            <Tooltip
                {...propsTooltip}
                classes={{ popper: className }}
                PopperProps={{ sx: { '.MuiTooltip-tooltip': { bgcolor: 'green' } } }}
            />
        ),
    )(() => ({
        [`& .${tooltipClasses.tooltip}`]: {
            backgroundColor: theme.palette.common.white,
            color: theme.palette.text.primary,
            boxShadow: theme.palette.background.shadow,
            textAlign: 'justify',
            padding: '15px',
            marginBottom: '5px',
        },
    }));

    const [tooltipOpen, setTooltipOpen] = useState(false);

    const handleTooltipClose = () => {
        setTooltipOpen(false);
    };

    const handleTooltipToggle = () => {
        setTooltipOpen(!tooltipOpen);
    };

    return (
        <Box
            sx={{
                marginBottom: '5px',
                marginTop: '15px',
                display: 'block',
                flexDirection: 'row',
                justifyContent: 'space-between',
            }}
        >
            <Typography style={{ display: 'inline', paddingTop: '40px', fontWeight: '500' }}>
                Veuillez ajouter toutes les modifications de dirigeants nécessaires :
            </Typography>
            {props.tooltip ? (
                <LightTooltip
                    key={props.tooltip.name}
                    title={
                        <Typography
                            variant="l2dbodyxs"
                            color="text.primary"
                            align="justify"
                        >
                            {props.tooltip.text}
                        </Typography>
                    }
                    open={tooltipOpen}
                    onClose={handleTooltipClose}
                    disableFocusListener
                    disableHoverListener
                    disableTouchListener
                    placement="top-end"
                >
                    <IconButton
                        color="secondary"
                        size="small"
                        sx={{
                            height: 'fit-content',
                            marginTop: '10px',
                        }}
                        onClick={handleTooltipToggle}
                    >
                        <FaOptionIcon {...informationIcon.props} />
                    </IconButton>
                </LightTooltip>
            ) : null}
            <GestionDirigeantList
                items={(formContext.getValues(props.name) || []).map(
                    (dirig: { gestionDirigeant: GestionDirigeant }) => {
                        const isNew = dirig.gestionDirigeant?.statutPourLaFormalite?.value === '1';

                        if (mode === 'adresse') {
                            return {
                                ...dirig,
                                actions: [
                                    {
                                        name: 'preview-adresse',
                                        text: "Modifier l'adresse du dirigeant",
                                        onClick: onPreviewAdresse,
                                    },
                                ],
                            };
                        }

                        return {
                            ...dirig,
                            actions: isNew
                                ? [
                                      {
                                          name: 'delete',
                                          text: 'Supprimer ce nouveau dirigeant',
                                          onClick: (_: GestionDirigeant, i: number) => {
                                              const newValues = [
                                                  ...formContext.getValues(props.name),
                                              ];
                                              newValues.splice(i, 1);
                                              formContext.setValue(props.name, newValues);
                                          },
                                      },
                                  ]
                                : [
                                      {
                                          name: 'preview',
                                          text: 'Modifier ce dirigeant',
                                          onClick: onPreview,
                                      },
                                  ],
                        };
                    },
                )}
            />
            {mode !== 'adresse' && (
                <Grid
                    container
                    spacing={2}
                >
                    <Grid
                        item
                        sm={12}
                        md={12}
                        lg={6}
                        xl={6}
                    />
                    <Grid
                        item
                        sm={12}
                        md={12}
                        lg={6}
                        xl={6}
                    >
                        <Button
                            onClick={onAdd}
                            color="secondary"
                            style={{
                                width: '100%',
                                marginTop: '30px',
                                padding: '5px 15px',
                                fontSize: '1rem',
                                borderRadius: '15px',
                                right: '0',
                                border: '1px solid #aaa',
                                backgroundColor: theme.palette.background.paper,
                                color: theme.palette.secondary.main,
                            }}
                        >
                            <FaOptionIcon
                                size="sm"
                                data-testid="test-id-mimetype-icone"
                                icon={faUserPlus}
                                color={theme.palette.secondary.main}
                                sx={{ margin: '0 5px' }}
                            />
                            Ajouter un·e dirigeant·e
                        </Button>
                    </Grid>
                </Grid>
            )}
        </Box>
    );
};
